/**
 * Pure game rules. No I/O, no DOM, no storage -- every function here is a
 * deterministic transform over `GameState` (except the explicitly random
 * `shuffle` / `drawAxes` / `assignSquares`, which take an RNG).
 *
 * Both the client and the Durable Object import this module, so the optimistic
 * UI and the authoritative server compute identical results. In the Firebase
 * original these rules were tangled into DOM-rendering functions and could only
 * run in a browser.
 */

import type {
  Axis,
  FinalTwist,
  GameState,
  Player,
  QuarterIndex,
  TeamKey,
} from './types';
import { leagueMeta, periodLabel, shortPeriodLabel } from './sports';
import { floorMoney, roundMoney } from './money';

export const GRID_SIZE = 100;
export const AXIS_LENGTH = 10;

/** Player swatch colors, cycled in order as players are added. */
export const COLOR_POOL = [
  '#e63946', '#2a9d8f', '#e9c46a', '#f4a261', '#8ecae6',
  '#a06cd5', '#06d6a0', '#ef476f', '#118ab2', '#ffd166',
  '#f78c6b', '#7bdff2', '#b388eb', '#84a59d', '#f28482',
] as const;

export type Rng = () => number;

/** Crypto-backed RNG. The original used `Math.random()` for real-money draws. */
export const secureRng: Rng = () => {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0]! / 2 ** 32;
};

/** Fisher-Yates. Returns a new array; does not mutate the input. */
export function shuffle<T>(input: readonly T[], rng: Rng = secureRng): T[] {
  const out = input.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function identityAxis(): Axis {
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
}

export function drawAxes(rng: Rng = secureRng): Record<TeamKey, Axis> {
  return { home: shuffle(identityAxis(), rng), away: shuffle(identityAxis(), rng) };
}

/**
 * Validates a manually entered axis: exactly 10 unique digits 0-9, in any
 * separator format. Returns null when invalid.
 */
export function parseAxisInput(raw: string): Axis | null {
  const parts = String(raw ?? '')
    .split(/[^0-9]+/)
    .filter(Boolean)
    .map((v) => Number.parseInt(v, 10));
  if (parts.length !== AXIS_LENGTH) return null;
  if (new Set(parts).size !== AXIS_LENGTH) return null;
  if (parts.some((n) => !Number.isInteger(n) || n < 0 || n > 9)) return null;
  return parts;
}

export function isValidAxis(axis: unknown): axis is Axis {
  return (
    Array.isArray(axis) &&
    axis.length === AXIS_LENGTH &&
    new Set(axis).size === AXIS_LENGTH &&
    axis.every((n) => Number.isInteger(n) && n >= 0 && n <= 9)
  );
}

/** Grid index for a (row, column) pair. Row = home axis, column = away axis. */
export function cellIndex(row: number, col: number): number {
  return row * AXIS_LENGTH + col;
}

/**
 * Maps a pair of score digits to the owning player id.
 *
 * The digits are looked up *through* the axes: a square's position is fixed,
 * but which digits it represents depends on the shuffle. Returns null when the
 * axes are still hidden or the square is unsold.
 */
export function ownerByDigits(
  state: Pick<GameState, 'axes' | 'axesRevealed' | 'grid'>,
  homeDigit: number,
  awayDigit: number,
): string | null {
  if (!state.axesRevealed) return null;
  const row = state.axes.home.indexOf(homeDigit);
  const col = state.axes.away.indexOf(awayDigit);
  if (row < 0 || col < 0) return null;
  return state.grid[cellIndex(row, col)] ?? null;
}

/**
 * How many times this board pays out: four quarters, or two halves.
 *
 * Read from `locked`, which is sized when the game is created, rather than
 * from the league. A game's shape must not change under it if the league
 * metadata is ever corrected -- people have already been paid by then.
 */
export function periodCount(state: Pick<GameState, 'locked'>): number {
  return state.locked.length;
}

/** Lowest checkpoint not yet ended, or null when all of them are done. */
export function currentQuarter(state: Pick<GameState, 'locked'>): QuarterIndex | null {
  for (let q = 0; q < periodCount(state); q++) {
    if (!state.locked[q]) return q;
  }
  return null;
}

/** Highest checkpoint already ended, or null when none are. */
export function lastLockedQuarter(state: Pick<GameState, 'locked'>): QuarterIndex | null {
  for (let q = periodCount(state) - 1; q >= 0; q--) {
    if (state.locked[q]) return q;
  }
  return null;
}

export function totalPot(state: Pick<GameState, 'config'>): number {
  return roundMoney(GRID_SIZE * state.config.squareCost);
}

/** Sum of everything already awarded from logged scores in `everyscore` mode. */
export function paidOut(state: Pick<GameState, 'config' | 'scoreLog'>): number {
  const paidCount = state.scoreLog.filter((s) => s.paid).length;
  return paidCount * state.config.payouts.everyscore.perScore;
}

export function potRemaining(state: Pick<GameState, 'config' | 'scoreLog'>): number {
  return totalPot(state) - paidOut(state);
}

/**
 * Payout per checkpoint. In `everyscore` mode every entry is `perScore`.
 *
 * Length follows the league: four for quarters, two for halves.
 */
export function payoutAmounts(
  state: Pick<GameState, 'config'>,
): number[] {
  const count = leagueMeta(state.config.league).periodCount;
  if (state.config.mode === 'standard') {
    return [...state.config.payouts.standard];
  }
  const per = state.config.payouts.everyscore.perScore;
  return Array<number>(count).fill(per);
}

/**
 * The end-of-game bonus in `everyscore` mode.
 *
 * Note the digits are the *digit sums* of the final scores mod 10 -- not the
 * scores' last digits. A 24-17 final gives home 2+4=6 and away 1+7=8. This is
 * carried over verbatim from the original, where it was the house rule that
 * made the twist unpredictable. The unpaid remainder splits 75% to the square
 * at (home, away) and the rest to the transposed square at (away, home).
 *
 * Returns null when the twist is disabled or the pot is fully paid out.
 */
export function computeFinalTwist(
  state: Pick<GameState, 'config' | 'scoreLog' | 'axes' | 'axesRevealed' | 'grid'>,
  homeTotal: number,
  awayTotal: number,
  now: Date = new Date(),
): FinalTwist | null {
  if (!state.config.payouts.everyscore.finalTwist) return null;
  const remaining = potRemaining(state);
  if (remaining <= 0) return null;

  const digitSum = (val: number) =>
    String(Math.abs(val))
      .split('')
      .reduce((sum, d) => sum + Number.parseInt(d, 10), 0);

  const homeDigit = digitSum(homeTotal) % 10;
  const awayDigit = digitSum(awayTotal) % 10;
  // Floored to the cent, not to the dollar. On a penny board the remainder
  // can be well under a dollar, and flooring to whole dollars handed the
  // actual square nothing and the inverse the lot -- the split backwards.
  const actualAmount = floorMoney(remaining * 0.75);

  return {
    homeTotal,
    awayTotal,
    homeDigit,
    awayDigit,
    actualOwnerId: ownerByDigits(state, homeDigit, awayDigit),
    inverseOwnerId: ownerByDigits(state, awayDigit, homeDigit),
    actualAmount,
    inverseAmount: roundMoney(remaining - actualAmount),
    remaining,
    awardedAt: now.toISOString(),
  };
}

/**
 * Randomly distributes unassigned squares to players who are owed them.
 *
 * Idempotent with respect to squares already on the grid: a player owed 5 who
 * already holds 2 receives 3. Over-subscription is truncated at the number of
 * empty squares rather than throwing.
 */
export function assignSquares(
  state: Pick<GameState, 'grid' | 'players'>,
  rng: Rng = secureRng,
): { grid: (string | null)[]; assigned: number } {
  const held = new Map<string, number>();
  for (const cell of state.grid) {
    if (cell) held.set(cell, (held.get(cell) ?? 0) + 1);
  }

  const queue: string[] = [];
  for (const p of state.players) {
    const need = p.count - (held.get(p.id) ?? 0);
    for (let i = 0; i < need; i++) queue.push(p.id);
  }

  const empties = shuffle(
    state.grid.reduce<number[]>((acc, cell, i) => {
      if (cell === null) acc.push(i);
      return acc;
    }, []),
    rng,
  );

  const grid = state.grid.slice();
  const assigned = Math.min(queue.length, empties.length);
  for (let i = 0; i < assigned; i++) {
    grid[empties[i]!] = queue[i]!;
  }
  return { grid, assigned };
}

export function squaresSold(state: Pick<GameState, 'grid'>): number {
  return state.grid.filter((c) => c !== null).length;
}

/**
 * Squares already spoken for, whether or not they are on the board yet.
 *
 * A player is committed by their `count` the moment they are added -- the grid
 * only fills in when the admin runs the random assignment. Measuring
 * availability by grid occupancy meant a fully sold pool still reported 100
 * squares free, so more could be sold and `assignSquares` would silently drop
 * the excess at `Math.min(queue.length, empties.length)`. People would have
 * paid for squares that never appeared.
 *
 * Manual placement can also put someone on more cells than their count, so the
 * larger of the two is the honest figure.
 */
export function squaresCommitted(
  state: Pick<GameState, 'grid' | 'players'>,
): number {
  const owed = state.players.reduce((sum, p) => sum + Math.max(0, p.count), 0);
  return Math.max(owed, squaresSold(state));
}

export function squaresAvailable(
  state: Pick<GameState, 'grid' | 'players'>,
): number {
  return Math.max(0, GRID_SIZE - squaresCommitted(state));
}

export interface WinningSquare {
  index: number;
  /** A locked/logged result, versus a live in-progress score. */
  kind: 'final' | 'potential';
  quarter: QuarterIndex | null;
  ownerId: string | null;
  homeDigit: number;
  awayDigit: number;
  amount: number;
}

/**
 * Every square currently highlighted as a winner, replacing the original's
 * direct `classList.add('winner')` DOM mutation.
 */
export function winningSquares(state: GameState): WinningSquare[] {
  if (!state.axesRevealed) return [];
  const out: WinningSquare[] = [];
  const amounts = payoutAmounts(state);

  const locate = (home: number, away: number) => {
    const row = state.axes.home.indexOf(home);
    const col = state.axes.away.indexOf(away);
    if (row < 0 || col < 0) return null;
    const index = cellIndex(row, col);
    return { index, ownerId: state.grid[index] ?? null };
  };

  if (state.config.mode === 'everyscore') {
    for (const score of state.scoreLog) {
      const homeDigit = score.home % 10;
      const awayDigit = score.away % 10;
      const hit = locate(homeDigit, awayDigit);
      if (!hit) continue;
      out.push({
        ...hit,
        kind: 'final',
        quarter: null,
        homeDigit,
        awayDigit,
        amount: score.paid ? state.config.payouts.everyscore.perScore : 0,
      });
    }
    return out;
  }

  for (let q = 0; q < periodCount(state); q++) {
    if (!state.locked[q]) continue;
    const quarterScore = state.scores[q]!;
    if (quarterScore.home === null || quarterScore.away === null) continue;
    const homeDigit = quarterScore.home % 10;
    const awayDigit = quarterScore.away % 10;
    const hit = locate(homeDigit, awayDigit);
    if (!hit) continue;
    out.push({
      ...hit,
      kind: 'final',
      quarter: q,
      homeDigit,
      awayDigit,
      amount: amounts[q] ?? 0,
    });
  }

  const live = state.liveScore;
  if (live.home !== null && live.away !== null) {
    const homeDigit = live.home % 10;
    const awayDigit = live.away % 10;
    const hit = locate(homeDigit, awayDigit);
    const q = currentQuarter(state);
    if (hit && q !== null && !out.some((w) => w.index === hit.index)) {
      out.push({
        ...hit,
        kind: 'potential',
        quarter: q,
        homeDigit,
        awayDigit,
        amount: amounts[q]!,
      });
    }
  }

  return out;
}

export interface PlayerEarnings {
  player: Player;
  /** Money actually won from locked quarters or paid logged scores. */
  earned: number;
  /** Money in play on the current live score. Always 0 in `everyscore` mode. */
  potential: number;
  /** Standard mode only; index = quarter. */
  /** Earnings per checkpoint. One entry per period. */
  byQuarter: number[];
}

/**
 * Per-player winnings, sorted by total (earned + potential) descending.
 * Replaces the original `renderTracker`'s inline accumulation over names.
 */
export function playerEarnings(state: GameState): PlayerEarnings[] {
  const amounts = payoutAmounts(state);
  const rows = new Map<string, PlayerEarnings>(
    state.players.map((player) => [
      player.id,
      {
        player,
        earned: 0,
        potential: 0,
        byQuarter: Array<number>(periodCount(state)).fill(0),
      },
    ]),
  );

  const credit = (id: string | null, amount: number, quarter?: QuarterIndex) => {
    if (!id) return;
    const row = rows.get(id);
    if (!row) return;
    row.earned += amount;
    if (quarter !== undefined && row.byQuarter[quarter] !== undefined) {
      row.byQuarter[quarter] += amount;
    }
  };

  if (state.config.mode === 'everyscore') {
    for (const score of state.scoreLog) {
      if (!score.paid) continue;
      credit(
        ownerByDigits(state, score.home % 10, score.away % 10),
        state.config.payouts.everyscore.perScore,
      );
    }
    if (state.finalTwist) {
      credit(state.finalTwist.actualOwnerId, state.finalTwist.actualAmount);
      credit(state.finalTwist.inverseOwnerId, state.finalTwist.inverseAmount);
    }
  } else {
    for (let q = 0; q < periodCount(state); q++) {
      if (!state.locked[q]) continue;
      const quarterScore = state.scores[q]!;
      if (quarterScore.home === null || quarterScore.away === null) continue;
      credit(
        ownerByDigits(state, quarterScore.home % 10, quarterScore.away % 10),
        amounts[q] ?? 0,
        q,
      );
    }

    const live = state.liveScore;
    const q = currentQuarter(state);
    if (live.home !== null && live.away !== null && q !== null) {
      const ownerId = ownerByDigits(state, live.home % 10, live.away % 10);
      const row = ownerId ? rows.get(ownerId) : undefined;
      if (row) row.potential = amounts[q]!;
    }
  }

  return [...rows.values()].sort(
    (a, b) => b.earned + b.potential - (a.earned + a.potential),
  );
}

export type GamePhase =
  | { kind: 'not-started'; label: string }
  | { kind: 'in-progress'; label: string }
  | { kind: 'final'; label: string };

/**
 * What to show where the quarter label goes.
 *
 * `currentQuarter` returns the first *unlocked* quarter, which is Q1 for a
 * brand-new board -- so a game nobody has touched read "1st Quarter", implying
 * it was underway. This distinguishes "not started" from "actually playing".
 *
 * A live feed reporting `in` is the strongest signal there is, so it wins; the
 * clock comes from it too. Otherwise anything recorded -- a locked quarter, a
 * score typed in, a logged score -- means play has begun.
 */
export function gamePhase(state: GameState): GamePhase {
  if (state.locked.every(Boolean)) return { kind: 'final', label: 'Final' };

  const meta = leagueMeta(state.config.league);
  const live = state.live;
  if (live?.state === 'in') {
    // The feed's period is one-based; ours is zero-based.
    const period = shortPeriodLabel(state.config.league, live.period - 1);
    return {
      kind: 'in-progress',
      label: live.displayClock ? `${period} · ${live.displayClock}` : period,
    };
  }

  const started =
    state.locked.some(Boolean) ||
    state.scoreLog.length > 0 ||
    state.liveScore.home !== null ||
    state.liveScore.away !== null;

  if (!started) return { kind: 'not-started', label: 'Not started' };

  const q = currentQuarter(state);
  return {
    kind: 'in-progress',
    label: q === null ? 'Final' : periodLabel(state.config.league, q),
  };
}



// ---------------------------------------------------------------------------
// Where a game is in its life
// ---------------------------------------------------------------------------

/** Mirrors the `status` values the game index accepts, minus 'archived'. */
export type GameStatus = 'setup' | 'open' | 'live' | 'final';

/**
 * The game's status, derived from its own state.
 *
 * The index previously stored 'setup' at creation and never changed it, so
 * every card in the list claimed to be unstarted forever. Deriving it here
 * keeps the one source of truth in the Durable Object, which writes the result
 * back whenever it changes.
 *
 * Archiving is deliberately not a status -- it is a separate timestamp, because
 * a game can be shelved at any point in its life.
 */
export function deriveStatus(state: GameState): GameStatus {
  if (state.locked.every(Boolean)) return 'final';
  if (state.axesRevealed) return 'live';
  if (state.players.length > 0 || squaresSold(state) > 0) return 'open';
  return 'setup';
}

export interface SetupStep {
  id: 'teams' | 'deadline' | 'players' | 'squares' | 'reveal';
  label: string;
  hint: string;
  done: boolean;
}

/**
 * The admin's path from an empty board to kickoff.
 *
 * A fresh game is a blank 10x10 grid with hidden numbers and no players, which
 * says nothing about what to do next. These are the four steps, in the order
 * they have to happen.
 */
export function setupChecklist(state: GameState): SetupStep[] {
  const teamsChosen =
    state.config.source !== null ||
    state.config.teams.home.name !== 'Home' ||
    state.config.teams.away.name !== 'Away';

  return [
    {
      id: 'teams',
      label: 'Choose the teams',
      hint: 'Pick a real matchup, or set the two teams by hand.',
      done: teamsChosen,
    },
    {
      id: 'deadline',
      label: 'Set the request deadline',
      hint: 'When entries close. Players see it in their own timezone.',
      done: Boolean(state.config.requestDeadline),
    },
    {
      id: 'players',
      label: 'Add players',
      hint: 'Add them yourself, or share the invite link and approve requests.',
      done: state.players.length > 0,
    },
    {
      id: 'squares',
      label: 'Assign squares',
      hint: 'Scatters each player across the board at random.',
      done: squaresSold(state) > 0,
    },
    {
      id: 'reveal',
      label: 'Reveal the numbers',
      hint: 'Do this at kickoff. It cannot be undone.',
      done: state.axesRevealed,
    },
  ];
}

/** True once every setup step is done and the checklist can disappear. */
export function isSetUp(state: GameState): boolean {
  return setupChecklist(state).every((step) => step.done);
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** What one player owes: their squares at the board's price. */
export function amountOwed(
  state: Pick<GameState, 'config'>,
  player: Player,
): number {
  return roundMoney(Math.max(0, player.count) * state.config.squareCost);
}

export interface PaymentSummary {
  /** Total the board should collect if every square is sold. */
  expected: number;
  collected: number;
  outstanding: number;
  paidCount: number;
  unpaidCount: number;
}

/** Who has settled up, for the admin running the pool. */
export function paymentSummary(state: GameState): PaymentSummary {
  let collected = 0;
  let outstanding = 0;
  let paidCount = 0;

  for (const player of state.players) {
    const owed = amountOwed(state, player);
    if (player.paidAt) {
      collected += owed;
      paidCount += 1;
    } else {
      outstanding += owed;
    }
  }

  return {
    expected: collected + outstanding,
    collected,
    outstanding,
    paidCount,
    unpaidCount: state.players.length - paidCount,
  };
}

/**
 * Total the quarter payouts promise.
 *
 * Only meaningful in standard mode; everyscore pays per logged score until the
 * pot runs out, which `potRemaining` already bounds.
 */
export function totalPromised(state: Pick<GameState, 'config'>): number {
  return state.config.payouts.standard.reduce((sum, n) => sum + n, 0);
}

/**
 * Whether the payouts fit the money the board can hold.
 *
 * Promising more than 100 squares can collect is not a preference, it is a
 * pool that cannot settle. Enforced server-side rather than warned about.
 */
export function payoutsFitPot(state: Pick<GameState, 'config'>): boolean {
  return totalPromised(state) <= totalPot(state);
}
